import React from 'react';
import { useTranslation } from 'react-i18next';
import { CropSelect } from './form/CropSelect';
import { VendorSelect } from './form/VendorSelect';
import { QuantityInput } from './form/QuantityInput';
import { PriceInput } from './form/PriceInput';
import Button from '../ui/Button';
import { SaleFormContentProps } from './types';
import VendorFormModal from '../vendors/VendorFormModal';

export const SaleFormContent: React.FC<SaleFormContentProps> = ({ 
  values, 
  errors, 
  onChange,
  showVendorModal,
  setShowVendorModal,
  disabled = false
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      {/* First Row - Crop and Vendor Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <CropSelect
            value={values.crop_id}
            onChange={onChange}
            error={errors.crop_id}
            disabled={disabled}
          />
        </div>
        
        <div className="space-y-2">
          <VendorSelect
            value={values.vendor_id}
            onChange={onChange}
            error={errors.vendor_id}
            disabled={disabled}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setShowVendorModal(true)}
            disabled={disabled}
          >
            {t('vendors.addVendor')}
          </Button>
        </div>
      </div>

      {/* Second Row - Quantity and Price */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <QuantityInput
          quantity={values.quantity}
          unit={values.unit}
          onChange={onChange}
          error={errors.quantity}
          disabled={disabled}
        />

        <PriceInput
          unitPrice={values.unit_price}
          priceUnit={values.price_unit}
          purchasePrice={values.purchase_price}
          onChange={onChange}
          errors={{
            unit_price: errors.unit_price,
            purchase_price: errors.purchase_price
          }}
          disabled={disabled}
        />
      </div>

      {/* Third Row - Date and Notes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="sale_date" className="block text-sm font-medium text-gray-700">
            {t('sales.saleDate')} *
          </label>
          <input
            type="date"
            id="sale_date"
            name="sale_date"
            value={values.sale_date}
            onChange={onChange}
            disabled={disabled}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
            required
          />
          {errors.sale_date && (
            <p className="mt-1 text-sm text-red-600">{errors.sale_date}</p>
          )}
        </div>

        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
            {t('sales.salesNotes')}
          </label>
          <textarea
            id="notes"
            name="notes"
            rows={3}
            value={values.notes}
            onChange={onChange}
            disabled={disabled}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
          />
        </div>
      </div>

      {showVendorModal && (
        <VendorFormModal
          onClose={() => setShowVendorModal(false)}
          onSuccess={() => setShowVendorModal(false)}
        />
      )}
    </div>
  );
};

export default SaleFormContent;